import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { useHostMode, useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { useCreateCustomMarkerMutation, useResetAllFoundPinsMutation, useTogglePinFoundMutation } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector, useAppStore } from '@repo/store/maps/hooks';
import { selectHighlightedPinIds } from '@repo/store/maps/slices/clientContext';
import { clearSelectedMarker, selectCurrentMapInfo, selectExternalMapId, selectFoundPins, selectMapData, selectMarker, selectSelectedMarkerId, selectTitleSlug } from '@repo/store/maps/slices/mapData';
import type { SelectLocationSource } from '@repo/types/analytic-events/maps-analytic-events';
import { CatalogGameMapFlags } from '@repo/types/catalog';
import { HostMode, HostType } from '@repo/types/host';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { hasFlag } from '@repo/utility/flags';
import type * as MapLibre from 'maplibre-gl';
import { useCallback, useEffect, useRef } from 'react';
import { useMapContext } from '../map-context';
import { isMapActive } from '../map-lifecycle';
export function useMapPinOperations() {
  const $ = _c(65);
  const {
    map
  } = useMapContext();
  const mapRef = useRef(map);
  const dispatch = useAppDispatch();
  const analytics = useAnalytics();
  const hostType = useHostType();
  const hostMode = useHostMode();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["game_map"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useT(t0);
  let t1;
  let t2;
  if ($[1] !== map) {
    t1 = () => {
      mapRef.current = map;
    };
    t2 = [map];
    $[1] = map;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const mapInfo = useAppSelector(selectCurrentMapInfo);
  const titleSlug = useAppSelector(selectTitleSlug);
  const externalId = useAppSelector(selectExternalMapId);
  const foundPins = useAppSelector(selectFoundPins);
  const mapData = useAppSelector(selectMapData);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const selectedMarkerIdRef = useRef(selectedMarkerId);
  const mapDataRef = useRef(mapData);
  const store = useAppStore();
  let t3;
  let t4;
  if ($[4] !== mapData) {
    t3 = () => {
      mapDataRef.current = mapData;
    };
    t4 = [mapData];
    $[4] = mapData;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const [togglePinFound, t5] = useTogglePinFoundMutation();
  const {
    isLoading: isTogglingPin
  } = t5;
  const [resetAllPins, t6] = useResetAllFoundPinsMutation();
  const {
    isLoading: isResettingPins
  } = t6;
  const [createCustomMarker, t7] = useCreateCustomMarkerMutation();
  const {
    isLoading: isCreatingMarker
  } = t7;
  let t8;
  let t9;
  if ($[7] !== selectedMarkerId) {
    t8 = () => {
      if (selectedMarkerId) {
        selectedMarkerIdRef.current = selectedMarkerId;
      }
    };
    t9 = [selectedMarkerId];
    $[7] = selectedMarkerId;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  useEffect(t8, t9);
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = (sourceLayer, markerId, properties) => {
      const currentMap = mapRef.current;
      if (!isMapActive(currentMap)) {
        return;
      }
      const source = currentMap.getSource(sourceLayer) as MapLibre.GeoJSONSource;
      if (!source) {
        return;
      }
      const addOrUpdateProperties = Object.entries(properties).map(_temp);
      source.updateData({
        update: [{
          id: markerId,
          addOrUpdateProperties
        }]
      });
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const updateMarkerInMap = t10;
  let t11;
  if ($[11] !== analytics || $[12] !== dispatch || $[13] !== externalId || $[14] !== hostMode || $[15] !== hostType || $[16] !== mapInfo || $[17] !== store || $[18] !== titleSlug) {
    t11 = (markerId_0, options) => {
      const opts = typeof options === "boolean" ? {
        flyTo: options
      } : options ?? {};
      const flyTo = opts.flyTo ?? false;
      const providedFeature = opts.feature;
      const isNewlyCreated = opts.isNewlyCreated ?? false;
      const selectSource = opts.selectSource;
      selectedMarkerIdRef.current = markerId_0;
      dispatch(selectMarker(markerId_0));
      const url = new URL(window.location.href);
      url.searchParams.set("location", markerId_0);
      window.history.replaceState({}, "", url.toString());
      const currentMap_0 = mapRef.current;
      if (!isMapActive(currentMap_0)) {
        return;
      }
      const selectedMarkerSource = currentMap_0.getSource("selected-marker") as MapLibre.GeoJSONSource;
      if (!selectedMarkerSource) {
        return;
      }
      let selectedFeature = providedFeature ?? mapDataRef.current?.geojson.features.find(f => f.properties.externalId === markerId_0);
      if (!selectedFeature) {
        const markersSource = currentMap_0.getSource("markers") as MapLibre.GeoJSONSource | undefined;
        if (markersSource) {
          const sourceFeatures = currentMap_0.querySourceFeatures("markers") ?? [];
          const fromSource = sourceFeatures.find(f_0 => f_0.properties?.externalId === markerId_0);
          if (fromSource) {
            selectedFeature = fromSource as unknown as MapDataLocationFeature;
          }
        }
      }
      const features = selectedFeature ? [{
        type: "Feature" as const,
        geometry: selectedFeature.geometry,
        properties: {
          ...selectedFeature.properties,
          selected: true
        },
        id: selectedFeature.properties?.externalId ?? markerId_0
      }] : [];
      selectedMarkerSource.setData({
        type: "FeatureCollection",
        features
      });
      if (selectedFeature) {
        if (providedFeature && isNewlyCreated) {
          const markersSource_0 = currentMap_0.getSource("markers") as MapLibre.GeoJSONSource | undefined;
          if (markersSource_0) {
            const featureForMarkers = {
              type: "Feature" as const,
              geometry: selectedFeature.geometry,
              properties: {
                ...selectedFeature.properties,
                selected: false
              },
              id: selectedFeature.properties?.externalId ?? markerId_0
            };
            markersSource_0.updateData({
              add: [featureForMarkers]
            });
          }
        }
      }
      if (flyTo && selectedFeature?.geometry.coordinates) {
        currentMap_0.flyTo({
          center: selectedFeature?.geometry.coordinates,
          zoom: Math.max(currentMap_0.getZoom(), 12),
          duration: 1500
        });
      }
      if (analytics && externalId && hostType && selectSource) {
        const supportsTeleport = Boolean(selectedFeature?.properties?.supportsTeleport);
        const zoomLevel = currentMap_0.getZoom();
        analytics.event("game_map_select_location", {
          mapId: externalId,
          locationId: markerId_0,
          supportsTeleport,
          supportsLiveLocation: mapInfo ? hasFlag(mapInfo.flags, CatalogGameMapFlags.SupportsLiveLocation) : false,
          zoomLevel,
          isEmbed: hostMode === HostMode.EMBED && hostType === HostType.WEB,
          platform: hostType,
          titleId: mapInfo?.titleId,
          titleSlug: titleSlug ?? undefined,
          isHighlighted: selectHighlightedPinIds(store.getState()).includes(markerId_0),
          selectSource
        });
      }
    };
    $[11] = analytics;
    $[12] = dispatch;
    $[13] = externalId;
    $[14] = hostMode;
    $[15] = hostType;
    $[16] = mapInfo;
    $[17] = store;
    $[18] = titleSlug;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const selectMarkerInMap = t11;
  let t12;
  if ($[20] !== dispatch || $[21] !== selectedMarkerId) {
    t12 = () => {
      const markerToClear = selectedMarkerId || selectedMarkerIdRef.current;
      selectedMarkerIdRef.current = null;
      dispatch(clearSelectedMarker());
      const url_0 = new URL(window.location.href);
      url_0.searchParams.delete("location");
      window.history.replaceState({}, "", url_0.toString());
      const currentMap_1 = mapRef.current;
      if (!markerToClear || !isMapActive(currentMap_1)) {
        return;
      }
      const selectedMarkerSource_0 = currentMap_1.getSource("selected-marker") as MapLibre.GeoJSONSource;
      if (selectedMarkerSource_0) {
        selectedMarkerSource_0.setData({
          type: "FeatureCollection",
          features: []
        });
      }
    };
    $[20] = dispatch;
    $[21] = selectedMarkerId;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const clearSelectedMarkerInMap = t12;
  let t13;
  if ($[23] !== selectedMarkerId) {
    t13 = (markerId_1, found) => {
      updateMarkerInMap("markers", markerId_1, {
        found
      });
      if (selectedMarkerId === markerId_1 || selectedMarkerIdRef.current === markerId_1) {
        updateMarkerInMap("selected-marker", markerId_1, {
          found
        });
      }
    };
    $[23] = selectedMarkerId;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const updateMarkerFoundStatus = t13;
  let t14;
  if ($[25] !== mapData) {
    t14 = () => {
      const currentMap_2 = mapRef.current;
      if (!isMapActive(currentMap_2) || !mapData) {
        return;
      }
      const source_0 = currentMap_2.getSource("markers") as MapLibre.GeoJSONSource;
      if (!source_0) {
        return;
      }
      const updates = mapData.geojson.features.map(_temp2);
      source_0.updateData({
        update: updates
      });
      selectedMarkerIdRef.current = null;
    };
    $[25] = mapData;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const resetAllMarkersInMap = t14;
  let t15;
  if ($[27] !== analytics || $[28] !== externalId || $[29] !== foundPins || $[30] !== hostType || $[31] !== mapData?.geojson || $[32] !== togglePinFound || $[33] !== updateMarkerFoundStatus) {
    t15 = async (locationId, trigger, found_0) => {
      if (!externalId) {
        return;
      }
      const actualFoundState = foundPins[locationId] || false;
      const currentFoundState = found_0 ?? actualFoundState;
      const feature_0 = mapData?.geojson.features.find(f_1 => f_1.properties.externalId === locationId);
      const isCustomPin = feature_0?.properties.isCustomPin || false;
      updateMarkerFoundStatus(locationId, !currentFoundState);
      try {
        await togglePinFound({
          externalId,
          locationId,
          isCustomPin,
          currentFoundState
        }).unwrap();
        analytics.event("game_map_toggle_location_found", {
          mapId: externalId,
          locationId,
          found: !currentFoundState,
          trigger,
          platform: hostType
        });
      } catch {
        updateMarkerFoundStatus(locationId, actualFoundState);
      }
    };
    $[27] = analytics;
    $[28] = externalId;
    $[29] = foundPins;
    $[30] = hostType;
    $[31] = mapData?.geojson;
    $[32] = togglePinFound;
    $[33] = updateMarkerFoundStatus;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const handleTogglePin = t15;
  let t16;
  if ($[35] !== externalId || $[36] !== foundPins || $[37] !== resetAllMarkersInMap || $[38] !== resetAllPins || $[39] !== updateMarkerFoundStatus) {
    t16 = async () => {
      if (!externalId) {
        return;
      }
      const previousFoundStates = {
        ...foundPins
      };
      const previousSelectedId = selectedMarkerIdRef.current;
      resetAllMarkersInMap();
      ;
      try {
        await resetAllPins(externalId).unwrap();
      } catch (t17) {
        const error = t17;
        Object.entries(previousFoundStates).forEach(t18 => {
          const [locationId_0, found_1] = t18;
          if (found_1) {
            updateMarkerFoundStatus(locationId_0, true);
          }
        });
        if (previousSelectedId) {
          updateMarkerInMap("selected-marker", previousSelectedId, {
            selected: true
          });
          selectedMarkerIdRef.current = previousSelectedId;
        }
        throw error;
      }
    };
    $[35] = externalId;
    $[36] = foundPins;
    $[37] = resetAllMarkersInMap;
    $[38] = resetAllPins;
    $[39] = updateMarkerFoundStatus;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const handleResetAllPins = t16;
  let t17;
  if ($[41] !== t) {
    t17 = t(_temp3);
    $[41] = t;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const defaultCustomMarkerTitle = t17;
  let t18;
  if ($[43] !== createCustomMarker || $[44] !== defaultCustomMarkerTitle || $[45] !== externalId) {
    t18 = async (lng, lat, t19, t20, t21) => {
      const title = t19 === undefined ? defaultCustomMarkerTitle : t19;
      const description = t20 === undefined ? "" : t20;
      const color = t21 === undefined ? "#ff6b6b" : t21;
      if (!externalId) {
        return;
      }
      const result = await createCustomMarker({
        externalId,
        lng,
        lat,
        title,
        description,
        color
      }).unwrap();
      return result;
    };
    $[43] = createCustomMarker;
    $[44] = defaultCustomMarkerTitle;
    $[45] = externalId;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const handleCreateCustomMarker = t18;
  let t19;
  if ($[47] !== foundPins) {
    t19 = locationId_1 => foundPins[locationId_1] || false;
    $[47] = foundPins;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const isPinFound = t19;
  let t20;
  if ($[49] !== mapData?.geojson) {
    t20 = locationId_2 => mapData?.geojson.features.find(f_2 => f_2.properties.externalId === locationId_2);
    $[49] = mapData?.geojson;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const getPinDetails = t20;
  let t21;
  if ($[51] !== clearSelectedMarkerInMap || $[52] !== externalId || $[53] !== foundPins || $[54] !== getPinDetails || $[55] !== handleCreateCustomMarker || $[56] !== handleResetAllPins || $[57] !== handleTogglePin || $[58] !== isCreatingMarker || $[59] !== isPinFound || $[60] !== isResettingPins || $[61] !== isTogglingPin || $[62] !== resetAllMarkersInMap || $[63] !== selectMarkerInMap) {
    t21 = {
      togglePinFound: handleTogglePin,
      resetAllPins: handleResetAllPins,
      createCustomMarker: handleCreateCustomMarker,
      updateMarkerInMap,
      selectMarkerInMap,
      clearSelectedMarkerInMap,
      resetAllMarkersInMap,
      isPinFound,
      getPinDetails,
      isTogglingPin,
      isResettingPins,
      isCreatingMarker,
      foundPins,
      externalId
    };
    $[51] = clearSelectedMarkerInMap;
    $[52] = externalId;
    $[53] = foundPins;
    $[54] = getPinDetails;
    $[55] = handleCreateCustomMarker;
    $[56] = handleResetAllPins;
    $[57] = handleTogglePin;
    $[58] = isCreatingMarker;
    $[59] = isPinFound;
    $[60] = isResettingPins;
    $[61] = isTogglingPin;
    $[62] = resetAllMarkersInMap;
    $[63] = selectMarkerInMap;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  return t21;
}
function _temp3($) {
  return $.custom_marker_default_title;
}
function _temp2(feature) {
  return {
    id: feature.properties.externalId,
    addOrUpdateProperties: [{
      key: "found",
      value: false
    }, {
      key: "selected",
      value: false
    }]
  };
}
function _temp(t0) {
  const [key, value] = t0;
  return {
    key,
    value
  };
}